/**
 * A placed word on the grid, including its target (solution) word.
 */
export interface WordPlacement {
  word: string;
  startRow: number;
  startCol: number;
  direction: "across" | "down";
}

/**
 * A slot in the grid layout where a word must be placed.
 * Before filling, `word` is empty. After filling, it holds the chosen word.
 */
export interface WordSlot {
  startRow: number;
  startCol: number;
  direction: "across" | "down";
  length: number;
  word: string;
}

/**
 * The output puzzle format.
 */
export interface Puzzle {
  date: string;
  size: "7x7" | "9x9";
  difficulty: Difficulty;
  grid: (string | null)[][]; // shuffled starting state, null = black cell
  solution: (string | null)[][]; // solved grid for validation
  words: WordPlacement[];
  maxSwaps: number;
}

/**
 * Grid layout — which cells are active vs black.
 * true = active (letter), false = black.
 */
export type GridLayout = boolean[][];

/**
 * Difficulty level for puzzle generation.
 * Controls which words are more likely to be selected using a bell curve
 * centered on different frequency tiers. Seven levels map to days of the week
 * (Sunday = easiest, Saturday = hardest).
 */
export type Difficulty =
  | "chill"    // Sunday    — center 0.10
  | "easy"     // Monday    — center 0.22
  | "breezy"   // Tuesday   — center 0.34
  | "medium"   // Wednesday — center 0.48
  | "tricky"   // Thursday  — center 0.62
  | "hard"     // Friday    — center 0.76
  | "brutal";  // Saturday  — center 0.88

/** Ordered list of all difficulty levels, easiest to hardest. */
export const DIFFICULTIES: readonly Difficulty[] = [
  "chill", "easy", "breezy", "medium", "tricky", "hard", "brutal",
] as const;

/** Display labels for each difficulty level. */
export const DIFFICULTY_LABELS: Record<Difficulty, string> = {
  chill: "Chill",
  easy: "Easy",
  breezy: "Breezy",
  medium: "Medium",
  tricky: "Tricky",
  hard: "Hard",
  brutal: "Brutal",
};

/**
 * A seeded pseudo-random number generator interface.
 */
export interface PRNG {
  /** Returns a float in [0, 1) */
  next(): number;
  /** Returns an integer in [min, max) */
  nextInt(min: number, max: number): number;
  /** Shuffles an array in place */
  shuffle<T>(arr: T[]): T[];
}
